import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Heading, IconButton, useColorMode } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import PortfolioLightbox from './PortfolioLightbox';
import ThemePhoto from './ThemePhoto';
import { GALLERIES, Gallery } from '../data/photos';
import './Portfolio.css';

// Fotky v galeriích se mění v scripts/build-photos.mjs, ne tady.

// Jak široká je karta na stránce (Portfolio.css) – podle toho si prohlížeč
// vybere náhled, nebo velkou fotku (ThemePhoto). Od 1280 px se karty dělí
// o šířku řady: 100vw bez odsazení 2 × 4vw a mezer 1,5vw, děleno počtem.
//
// Na mobilu je karta 250–335 px, ale uvádíme schválně jen 266 px: náhled
// (800 px) pak stačí i na displeji s hustotou 3× (800 / 266 > 3). Jinak by
// iPhone (Safari bere nejmenší variantu, která pokryje celou hustotu) stáhl
// u všech karet velkou barevnou i černobílou verzi – ~3,5 MB místo ~0,5 MB –
// a rozdíl mezi 2,4× a 3× na telefonu stejně není vidět.
const MOBILE_CARD_CAP = 266;
const WIDE_CARD_VW =
  (100 - 8 - (GALLERIES.length - 1) * 1.5) / Math.max(GALLERIES.length, 1);
const CARD_SIZES =
  `(max-width: 768px) ${MOBILE_CARD_CAP}px, (max-width: 1279px) 320px, ` +
  `${WIDE_CARD_VW.toFixed(1)}vw`;

// ─── Karta jedné kategorie ────────────────────────────────────────────────
interface PortfolioCardProps {
  title: string;
  cover: string;
  onClick: () => void;
}

const PortfolioCard: React.FC<PortfolioCardProps> = ({ title, cover, onClick }) => (
  <Box
    as="button"
    className="portfolio-card"
    onClick={onClick}
    aria-label={`Otevřít galerii – ${title}`}
  >
    <Box className="portfolio-card-media">
      <ThemePhoto
        base={cover}
        alt={title}
        thumb
        sizes={CARD_SIZES}
        className="portfolio-card-photo"
      />
      <Box className="portfolio-card-veil" />
      <Box className="portfolio-card-caption">
        <span className="portfolio-card-rule" />
        <Heading as="h3" className="portfolio-card-title">
          {title}
        </Heading>
        <span className="portfolio-card-cta">Zobrazit galerii</span>
      </Box>
    </Box>
  </Box>
);

// ─── Sekce portfolia ──────────────────────────────────────────────────────
const Portfolio: React.FC = () => {
  const { colorMode } = useColorMode();
  const [openGallery, setOpenGallery] = useState<Gallery | null>(null);

  const railRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  // Hlídá, jestli je kam posouvat – podle toho se ukazují šipky a kraje.
  const updateArrows = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    const max = rail.scrollWidth - rail.clientWidth;
    setCanPrev(rail.scrollLeft > 4);
    setCanNext(rail.scrollLeft < max - 4);
  }, []);

  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [updateArrows]);

  const scrollByCards = (dir: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    const card = rail.querySelector<HTMLElement>('.portfolio-card');
    // Mezera mezi kartami se na širokém monitoru mění (Portfolio.css), proto ji čteme
    const gap = parseFloat(getComputedStyle(rail).columnGap) || 24;
    const step = card ? card.offsetWidth + gap : rail.clientWidth * 0.8;
    rail.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <>
      <Box
        as="section"
        className="portfolio-container"
        id="portfolio"
        bg={colorMode === 'dark' ? 'gray.800' : '#f8f8f8'}
      >
        <Heading
          as="h2"
          className="section-title reveal"
          color={colorMode === 'dark' ? 'white' : 'black'}
        >
          PORTFOLIO
        </Heading>

        <Box className={`portfolio-rail-wrap reveal${canPrev ? ' has-prev' : ''}${canNext ? ' has-next' : ''}`}>
          <IconButton
            aria-label="Předchozí"
            icon={<ChevronLeftIcon boxSize="24px" />}
            onClick={() => scrollByCards(-1)}
            className="portfolio-nav portfolio-nav--prev"
            variant="unstyled"
            tabIndex={canPrev ? 0 : -1}
          />

          <Box ref={railRef} className="portfolio-rail" onScroll={updateArrows}>
            {GALLERIES.map(gallery => (
              <PortfolioCard
                key={gallery.slug}
                title={gallery.title}
                cover={gallery.photos[0]}
                onClick={() => setOpenGallery(gallery)}
              />
            ))}
          </Box>

          <IconButton
            aria-label="Další"
            icon={<ChevronRightIcon boxSize="24px" />}
            onClick={() => scrollByCards(1)}
            className="portfolio-nav portfolio-nav--next"
            variant="unstyled"
            tabIndex={canNext ? 0 : -1}
          />
        </Box>
      </Box>

      {openGallery && (
        <PortfolioLightbox
          category={openGallery}
          onClose={() => setOpenGallery(null)}
        />
      )}
    </>
  );
};

export default Portfolio;
